/**
 * IDF Serializer
 *
 * Converts a structured IDFDocument back to valid IDF text.
 * Maintains consistent formatting and preserves comments.
 */

import type { IDFDocument, IDFField, IDFObject, SerializerOptions } from './types';

const DEFAULT_OPTIONS: Required<SerializerOptions> = {
  minified: false,
  indent: '  ',
  alignComments: true,
  commentColumn: 25,
  preserveFormatting: true,
};

/**
 * Serializer for IDF (Input Data File) format.
 */
export class IDFSerializer {
  private options: Required<SerializerOptions>;

  constructor(options: SerializerOptions = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Serialize an IDF document to text.
   */
  serialize(document: IDFDocument): string {
    const lines: string[] = [];

    // Header comments
    for (const comment of document.headerComments) {
      lines.push(this.formatComment(comment));
    }

    if (document.headerComments.length > 0) {
      lines.push('');
    }

    // Objects
    for (const [i, obj] of document.objects.entries()) {
      const objectLines = this.serializeObject(obj);
      lines.push(...objectLines);

      // Add blank line between objects (unless minified)
      if (!this.options.minified && i < document.objects.length - 1) {
        lines.push('');
      }
    }

    // Trailing comments
    if (document.trailingComments.length > 0) {
      lines.push('');
      for (const comment of document.trailingComments) {
        lines.push(this.formatComment(comment));
      }
    }

    return lines.join('\n');
  }

  /**
   * Serialize a single IDF object.
   */
  serializeObject(obj: IDFObject): string[] {
    const lines: string[] = [];

    // Leading comments
    for (const comment of obj.leadingComments) {
      lines.push(this.formatComment(comment));
    }

    if (this.options.minified) {
      // Minified: single line
      const values = obj.fields.map((f) => f.value).join(',');
      lines.push(`${obj.className},${values};`);
    } else {
      // Standard format with indentation
      lines.push(`${obj.className},`);

      for (const [i, field] of obj.fields.entries()) {
        const isLast = i === obj.fields.length - 1;
        const delimiter = isLast ? ';' : ',';

        const fieldLine = this.formatField(field, delimiter);
        lines.push(fieldLine);
      }

      // Handle objects with no fields
      if (obj.fields.length === 0) {
        // Remove trailing comma from class name and add semicolon
        const lastLine = lines[lines.length - 1];
        if (lastLine) {
          lines[lines.length - 1] = lastLine.slice(0, -1) + ';';
        }
      }
    }

    return lines;
  }

  /**
   * Format a single field with optional comment alignment.
   */
  private formatField(field: IDFField, delimiter: string): string {
    const { indent, alignComments, commentColumn } = this.options;

    let line = `${indent}${field.value}${delimiter}`;

    if (field.inlineComment) {
      if (alignComments) {
        // Pad to comment column
        const padding = Math.max(1, commentColumn - line.length);
        line += ' '.repeat(padding);
      } else {
        line += ' ';
      }
      line += `!- ${field.inlineComment}`;
    }

    return line;
  }

  /**
   * Format a comment line, ensuring it starts with !
   */
  private formatComment(comment: string): string {
    // If comment already starts with !, use as-is
    if (comment.startsWith('!')) {
      return comment;
    }
    return `! ${comment}`;
  }
}

/**
 * Convenience function to serialize an IDF document.
 */
export function serializeIDF(document: IDFDocument, options?: SerializerOptions): string {
  const serializer = new IDFSerializer(options);
  return serializer.serialize(document);
}

/**
 * Serialize a single object to IDF text.
 */
export function serializeObject(obj: IDFObject, options?: SerializerOptions): string {
  const serializer = new IDFSerializer(options);
  return serializer.serializeObject(obj).join('\n');
}
